// LSP `textDocument/completion` → CodeMirror autocomplete adapter.
//
// CodeMirror calls `CompletionSource` whenever the user types an
// identifier character, presses Ctrl-Space, or otherwise asks for
// suggestions. We delegate to the LSP, then translate each
// `CompletionItem` into a CodeMirror `Completion` entry. The `from`
// offset is the start of the current word so accepting a suggestion
// replaces the in-progress token rather than appending to it.
//
// The Nowline server returns plain `CompletionItem[]` with
// `insertText` filled in for everything that isn't pure keyword
// completion. Per `specs/lsp.md` and the m4.7 handoff, we treat
// `insertText` as the literal text to apply — no snippet expansion,
// no command resolution — because the client doesn't advertise
// `snippetSupport: true` on the initialize side.

import {
    autocompletion,
    type Completion,
    type CompletionContext,
    type CompletionResult,
    type CompletionSource,
} from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';
import { CompletionItemKind } from 'vscode-languageserver-protocol';
import type { LspClient } from '../client.ts';

export function lspAutocomplete(client: LspClient): Extension {
    const source: CompletionSource = async (
        context: CompletionContext,
    ): Promise<CompletionResult | null> => {
        // matchBefore picks up an in-progress identifier (including the
        // Nowline DSL's hyphenated atoms like `in-progress`). If the
        // cursor is at column 0 with no prefix and the request is
        // implicit, skip — CodeMirror's default behaviour is to wait
        // for at least one character before showing suggestions.
        const word = context.matchBefore(/[A-Za-z_][A-Za-z0-9_-]*/);
        if (!word && !context.explicit) return null;
        const from = word?.from ?? context.pos;

        const items = await client.completion(context.state, context.pos).catch(() => []);
        // The catch swallows the error and returns []; we deliberately
        // fall through to `null` below rather than `{ options: [] }` so
        // CodeMirror retries on the next keystroke instead of pinning
        // an empty popup.
        if (!items.length) return null;

        const options: Completion[] = items.map((item) => {
            const completion: Completion = {
                label: item.label,
                apply: item.insertText ?? item.label,
            };
            if (item.detail) completion.detail = item.detail;
            const type = kindToCmType(item.kind);
            if (type) completion.type = type;
            return completion;
        });

        return { from, options, validFor: /^[A-Za-z0-9_-]*$/ };
    };

    return autocompletion({
        override: [source],
        // Match VS Code's behaviour: trigger on every identifier-character
        // keystroke without waiting for Ctrl-Space.
        activateOnTyping: true,
    });
}

function kindToCmType(kind: CompletionItemKind | undefined): string | undefined {
    switch (kind) {
        case CompletionItemKind.Keyword:
            return 'keyword';
        case CompletionItemKind.Method:
        case CompletionItemKind.Function:
        case CompletionItemKind.Constructor:
            return 'function';
        case CompletionItemKind.Variable:
            return 'variable';
        case CompletionItemKind.Class:
        case CompletionItemKind.Module:
        case CompletionItemKind.Interface:
            return 'class';
        case CompletionItemKind.Property:
        case CompletionItemKind.Field:
            return 'property';
        case CompletionItemKind.Enum:
        case CompletionItemKind.EnumMember:
            return 'enum';
        case CompletionItemKind.Text:
            return 'text';
        case CompletionItemKind.Constant:
        case CompletionItemKind.Color:
            return 'constant';
        default:
            return undefined;
    }
}
